/**
 * Current user's active enrollments — lets the schedule know which classes
 * the user is already registered for (to show enroll vs. enrolled state).
 */

import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';

import { supabase } from '../../lib/supabase';
import {
  fetchMyEnrollments,
  enrollInClass,
  cancelEnrollment,
  checkIn,
} from '../../services/schedule';
import { useAuth } from '../../constants/providers/AuthProvider';

export function useMyEnrollments() {
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;
  const queryClient = useQueryClient();

  const queryKey = ['my-enrollments', userId];

  const query = useQuery({
    queryKey,
    queryFn: () => fetchMyEnrollments(userId!),
    enabled: !!userId,
    staleTime: 60 * 1000,
  });

  // Real-time: refresh my enrollments on any change
  useEffect(() => {
    if (!userId) return;

    let debounce: ReturnType<typeof setTimeout>;
    const channel = supabase
      .channel(`my-enrollments-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'class_enrollments' }, () => {
        clearTimeout(debounce);
        debounce = setTimeout(() => {
          queryClient.invalidateQueries({ queryKey });
        }, 500);
      })
      .subscribe();

    return () => {
      clearTimeout(debounce);
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derive a set of enrolled class IDs for O(1) lookup
  const enrolledClassIds = new Set(
    (query.data || []).map((e) => e.class_id)
  );

  const enrollmentByClassId = Object.fromEntries(
    (query.data || []).map((e) => [e.class_id, e])
  );

  const enrollMutation = useMutation({
    mutationFn: ({ classId }: { classId: string }) => {
      if (!userId) throw new Error('Not authenticated');
      return enrollInClass(classId, userId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
    },
  });

  const cancelMutation = useMutation({
    mutationFn: ({ enrollmentId }: { enrollmentId: string }) =>
      cancelEnrollment(enrollmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['schedule-events'] });
    },
  });

  const checkInMutation = useMutation({
    mutationFn: ({ enrollmentId }: { enrollmentId: string }) => checkIn(enrollmentId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  return {
    enrollments: query.data || [],
    enrolledClassIds,
    enrollmentByClassId,
    isLoading: query.isLoading,
    // Actions
    enroll: enrollMutation.mutateAsync,
    isEnrolling: enrollMutation.isPending,
    enrollError: enrollMutation.error as Error | null,
    cancel: cancelMutation.mutateAsync,
    isCancelling: cancelMutation.isPending,
    checkIn: checkInMutation.mutateAsync,
    isCheckingIn: checkInMutation.isPending,
  };
}
